/**
 * Turkish formatting — Document 6 §5.
 *
 * These functions are the single source of truth for every number and date the
 * product displays, in the web UI, the report preview and the PDF renderer
 * alike. Preview and PDF must produce byte-identical strings (Doc 6 §5.7), so
 * nothing here reads the ambient locale or relies on Intl defaults.
 */

/** U+2212 MINUS SIGN. Not the hyphen — Doc 6 §5.3. */
export const MINUS = '−';

/** En dash used in date ranges — Doc 6 §5.5. */
export const EN_DASH = '–';

export const MONTHS_TR = [
  'Ocak',
  'Şubat',
  'Mart',
  'Nisan',
  'Mayıs',
  'Haziran',
  'Temmuz',
  'Ağustos',
  'Eylül',
  'Ekim',
  'Kasım',
  'Aralık',
] as const;

export const MONTHS_TR_SHORT = [
  'Oca',
  'Şub',
  'Mar',
  'Nis',
  'May',
  'Haz',
  'Tem',
  'Ağu',
  'Eyl',
  'Eki',
  'Kas',
  'Ara',
] as const;

/**
 * Absolute value as a Turkish decimal string.
 *
 * Trailing zero decimals are dropped: 82.0 -> "82", 71.8 -> "71,8".
 * The sign is never included — callers decide how to present it, because a
 * value and a delta sign differently (Doc 6 §5.3).
 */
function absToTr(value: number, decimals: number): string {
  let out = Math.abs(value).toFixed(decimals);

  // Only strip zeros when a decimal point is actually present, otherwise
  // an integer like 100 would lose its own trailing zeros.
  if (out.includes('.')) {
    out = out.replace(/0+$/, '').replace(/\.$/, '');
  }

  const [intPart = '0', fracPart] = out.split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  return fracPart ? `${grouped},${fracPart}` : grouped;
}

/** True when the value rounds to zero at the given precision. */
function roundsToZero(value: number, decimals: number): boolean {
  return Math.abs(value) < 0.5 / Math.pow(10, decimals);
}

/**
 * A measured value with its unit.
 *
 *   formatValue(71.8, 'kg', 1)  -> "71,8 kg"
 *   formatValue(82, 'cm', 1)    -> "82 cm"
 *   formatValue(24.8, '%', 1)   -> "%24,8"
 *   formatValue(26.4, '', 1)    -> "26,4"
 *
 * The percent sign precedes the number, per Turkish convention (Doc 6 §5.2).
 */
export function formatValue(value: number, unit: string, decimals: number): string {
  const sign = value < 0 && !roundsToZero(value, decimals) ? MINUS : '';
  const n = absToTr(value, decimals);
  if (unit === '%') return `${sign}%${n}`;
  return unit ? `${sign}${n} ${unit}` : `${sign}${n}`;
}

/**
 * A change between two values. Always signed, never coloured (Doc 6 §5.3).
 *
 *   formatDelta(-2.4, 'kg', 1)  -> "−2,4 kg"
 *   formatDelta(1.2, '%', 1)    -> "+%1,2"
 *   formatDelta(0, 'cm', 1)     -> "0"
 */
export function formatDelta(value: number, unit: string, decimals: number): string {
  if (roundsToZero(value, decimals)) return '0';
  const sign = value < 0 ? MINUS : '+';
  const n = absToTr(value, decimals);
  if (unit === '%') return `${sign}%${n}`;
  return unit ? `${sign}${n} ${unit}` : `${sign}${n}`;
}

/** Percentage without a sign: formatPercent(62) -> "%62". */
export function formatPercent(value: number, decimals = 0): string {
  return `%${absToTr(value, decimals)}`;
}

interface DateParts {
  year: number;
  month: number;
  day: number;
}

/**
 * Parses a YYYY-MM-DD string without constructing a Date.
 *
 * `new Date('2026-08-01')` is parsed as UTC midnight and then rendered in the
 * viewer's local zone, which silently shifts the date by a day for anyone west
 * of UTC. Report periods are date-only and must never move (Doc 6 §5.6).
 */
function parseDateOnly(value: string): DateParts | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

/** "2026-08-01" -> "01 Ağustos 2026" */
export function formatDateLong(value: string): string {
  const d = parseDateOnly(value);
  if (!d) return value;
  const day = String(d.day).padStart(2, '0');
  return `${day} ${MONTHS_TR[d.month - 1]} ${d.year}`;
}

/** "2026-08-01" -> "01.08.2026" */
export function formatDateShort(value: string): string {
  const d = parseDateOnly(value);
  if (!d) return value;
  const day = String(d.day).padStart(2, '0');
  const month = String(d.month).padStart(2, '0');
  return `${day}.${month}.${d.year}`;
}

/** "01 Ağustos 2026 – 31 Ağustos 2026" */
export function formatPeriod(start: string, end: string): string {
  return `${formatDateLong(start)} ${EN_DASH} ${formatDateLong(end)}`;
}

/**
 * Chart axis label for a measurement date.
 * Full month name at 4 or fewer points, 3-letter form at 5 or more (Doc 6 §5.5).
 */
export function formatAxisLabel(value: string, short: boolean): string {
  const d = parseDateOnly(value);
  if (!d) return value;
  const table = short ? MONTHS_TR_SHORT : MONTHS_TR;
  return table[d.month - 1] ?? value;
}

/**
 * Age in whole years on a given reference date, both YYYY-MM-DD.
 * Frozen into the report snapshot at generation time (Doc 6 §16).
 */
export function calculateAge(dateOfBirth: string, on: string): number | null {
  const birth = parseDateOnly(dateOfBirth);
  const ref = parseDateOnly(on);
  if (!birth || !ref) return null;

  let age = ref.year - birth.year;
  if (ref.month < birth.month || (ref.month === birth.month && ref.day < birth.day)) {
    age -= 1;
  }
  return age >= 0 ? age : null;
}

/**
 * BMI from weight and height. Doc 6 §11.2.
 * Returns null rather than Infinity when height is missing or zero.
 */
export function calculateBmi(weightKg: number, heightCm: number): number | null {
  if (!Number.isFinite(weightKg) || !Number.isFinite(heightCm)) return null;
  if (weightKg <= 0 || heightCm <= 0) return null;
  const heightM = heightCm / 100;
  return weightKg / (heightM * heightM);
}

/**
 * Folds a name for searching: Turkish-aware lower case, then diacritics
 * removed.
 *
 *   "Ayşe Yılmaz"  ->  "ayse yilmaz"
 *   "AYŞE YILMAZ"  ->  "ayse yilmaz"
 *   "Ayse Yilmaz"  ->  "ayse yilmaz"
 *
 * Both the stored value and the search term go through this, so all three
 * spellings above find the same student. The Turkish locale matters on the
 * first step: only there does "I" lower-case to "ı" and "İ" to "i", which is
 * what makes "YILMAZ" and "Yilmaz" meet at the same folded form.
 */
export function foldForSearch(value: string): string {
  return value
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .replace(/ş/g, 's')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/â/g, 'a')
    .replace(/î/g, 'i')
    .replace(/û/g, 'u')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Chart axis label as day and month: "2026-05-02" -> "02.05".
 *
 * Used when a series has more than one point in the same month, where month
 * names alone would render as "Haz, Haz" and read as a duplicate rather than
 * as two separate measurements.
 */
export function formatAxisLabelDay(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return m ? `${m[3]}.${m[2]}` : value;
}
